'use client';

import { useState } from 'react';

import { useRouter } from 'next/navigation';

import { motion, type PanInfo } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { truncateText } from '@/features/v2/adapters/texts';

import { TEXT_SHARP_STYLES } from '@/utils/cardStyles';

import { ComingSoonBadge } from '@/components/ui/comingSoonBadge';

import { HButton } from '@/stories/atoms/buttons/mainButton/Button';
import { RegularCard } from '@/stories/molecules/cards/regularCard/RegularCard';

import { CardData, DecorativeCardData, CasinoCardData } from './interface';

import './decorativeCarousel.css';

const cardDefaults = [
  {
    id: '1',
    imageSrc: 'https://images.unsplash.com/photo-1528181304800-259b08848526?q=80&w=3870&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    title: 'Card 1',
    description: 'Description 1',
  },
  {
    id: '2',
    imageSrc: 'https://images.unsplash.com/photo-1506665531195-37a89d6b3095?q=80&w=3870&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    title: 'Card 2',
    description: 'Description 2',
  },
  {
    id: '3',
    imageSrc: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?q=80&w=3456&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    title: 'Card 3',
    description: 'Description 3',
  },
  {
    id: '4',
    imageSrc: 'https://images.unsplash.com/photo-1495103033382-fe343886b671?q=80&w=3870&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    title: 'Card 4',
    description: 'Description 4',
  },
  {
    id: '5',
    imageSrc: 'https://images.unsplash.com/photo-1506781961370-37a89d6b3095?q=80&w=3264&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    title: 'Card 5',
    description: 'Description 5',
  },
  {
    id: '6',
    imageSrc: 'https://images.unsplash.com/photo-1599576838688-8a6c11263108?q=80&w=3870&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    title: 'Card 6',
    description: 'Description 6',
  },
  {
    id: '7',
    imageSrc: 'https://images.unsplash.com/photo-1494094892896-7f14a4433b7a?q=80&w=3870&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    title: 'Card 7',
    description: 'Description 7',
  },
  {
    id: '8',
    imageSrc: 'https://plus.unsplash.com/premium_photo-1664910706524-e783eed89e71?q=80&w=3869&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    title: 'Card 8',
    description: 'Description 8',
  },
  {
    id: '9',
    imageSrc: 'https://images.unsplash.com/photo-1503788311183-fa3bf9c4bc32?q=80&w=3870&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    title: 'Card 9',
    description: 'Description 9',
  },
  {
    id: '10',
    imageSrc: 'https://images.unsplash.com/photo-1585970480901-90d6bb2a48b5?q=80&w=3774&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    title: 'Card 10',
    description: 'Description 10',
  },
];

interface CarouselProps {
  // eslint-disable-next-line react/require-default-props
  cards?: CardData[];
  // eslint-disable-next-line react/require-default-props
  cardType?: 'decorative' | 'casino';
  // eslint-disable-next-line react/require-default-props
  onCardClick?: (card: CardData) => void;
  // eslint-disable-next-line react/require-default-props
  customCardRenderer?: (card: CardData, position: keyof typeof positions) => React.ReactNode;
  // eslint-disable-next-line react/require-default-props
  comingSoon?: boolean;
}

const positions = {
  left: {
    x: '-65%',
    scale: 0.85,
    zIndex: 1,
    opacity: 0.7,
    rotateY: 15,
    filter: 'blur(2px) brightness(0.7)',
  },
  center: {
    x: '0%',
    scale: 1,
    zIndex: 5,
    opacity: 1,
    rotateY: 0,
    filter: 'blur(0px) brightness(1)',
  },
  right: {
    x: '65%',
    scale: 0.85,
    zIndex: 1,
    opacity: 0.7,
    rotateY: -15,
    filter: 'blur(2px) brightness(0.7)',
  },
  farLeft: {
    x: '-120%',
    scale: 0.7,
    zIndex: 0,
    opacity: 0,
    rotateY: 30,
    filter: 'blur(4px) brightness(0.5)',
  },
  farRight: {
    x: '120%',
    scale: 0.7,
    zIndex: 0,
    opacity: 0,
    rotateY: -30,
    filter: 'blur(4px) brightness(0.5)',
  },
} as const;

export function RollingGallery({
  cards = cardDefaults,
  cardType = 'decorative',
  onCardClick,
  customCardRenderer,
  comingSoon = false,
}: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const router = useRouter();

  const getCardPosition = (cardIndex: number) => {
    const diff = (cardIndex - currentIndex + cards.length) % cards.length;
    if (diff === 0) return 'center';
    if (diff === 1 || diff === -cards.length + 1) return 'right';
    if (diff === cards.length - 1 || diff === -1) return 'left';
    if (diff === 2 || diff === -cards.length + 2) return 'farRight';
    return 'farLeft';
  };

  const nextSlide = () => { return setCurrentIndex((prev) => { return (prev + 1) % cards.length; }); };

  const prevSlide = () => { return setCurrentIndex((prev) => { return (prev - 1 + cards.length) % cards.length; }); };

  const cardVariants = {
    initial: (custom: keyof typeof positions) => { return positions[custom]; },
    animate: (custom: keyof typeof positions) => { return positions[custom]; },
    exit: (custom: keyof typeof positions) => { return positions[custom]; },
  };

  const handleCardClick = (card: CardData) => {
    if (onCardClick) {
      onCardClick(card);
      return;
    }
    if (card.url) {
      router.push(card.url);
    }
  };

  const DRAG_DISTANCE_PX = 60;
  const DRAG_VELOCITY_PX_S = 300;

  const onDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const { offset, velocity } = info;
    if (offset.x <= -DRAG_DISTANCE_PX || velocity.x <= -DRAG_VELOCITY_PX_S) {
      nextSlide();
      return;
    }
    if (offset.x >= DRAG_DISTANCE_PX || velocity.x >= DRAG_VELOCITY_PX_S) {
      prevSlide();
    }
  };

  const renderDecorativeCard = (card: DecorativeCardData) => {
    return (
      <div className={comingSoon ? 'pointer-events-none' : ''}>
        <RegularCard
          contentAlign="left"
          imageCover={card.imageSrc}
          width={250}
          height={250}
          bgImgSize="lg"
          style={{ width: '250px', height: '250px' }}
          imageGrayscale={comingSoon}
          isFooterBlurred={false}
          footerContent={(
            <div 
              className="flex justify-between items-center gap-2 relative z-20" 
              style={TEXT_SHARP_STYLES}
            >
              <p className="text-white font-bold">
                {card.title}
              </p>
              <HButton
                disabled={comingSoon}
                label="Ver más"
                mode="secondary"
                size="medium"
                onPress={() => { return handleCardClick(card); }}
              />
            </div>
          )}
        />
      </div>
    );
  };

  const renderCasinoCard = (card: CasinoCardData) => {
    return (
      <div className={comingSoon ? 'pointer-events-none' : ''}>
        <RegularCard
          contentAlign="left"
          imageCover={card.imageSrc}
          width={250}
          height={250}
          bgImgSize="lg"
          style={{ width: '250px', height: '250px' }}
          imageGrayscale={comingSoon}
          isFooterBlurred={false}
          footerContent={(
            <div 
              className="flex gap-2 w-full h-10 relative z-20" 
              style={TEXT_SHARP_STYLES}
            >
              <div className="flex flex-col w-1/2">
                <p
                  className="text-white font-bold text-sm cursor-help"
                  title={card.title}
                >
                  {truncateText(card.title, 10)}
                </p>
                {card.provider && (
                  <p className="text-white/80 text-xs">
                    {card.provider}
                  </p>
                )}
              </div>
              <div className="min-w-1/2 pr-3">
                <HButton
                  disabled={comingSoon}
                  label="Jugar"
                  mode="secondary"
                  size="large"
                  onPress={() => { return handleCardClick(card); }}
                />
              </div>
            </div>
          )}
        />
      </div>
    );
  };

  const renderCard = (card: CardData, position: keyof typeof positions) => {
    if (customCardRenderer) {
      return customCardRenderer(card, position);
    }

    if (cardType === 'casino' && 'provider' in card) {
      return renderCasinoCard(card);
    }

    return renderDecorativeCard(card as DecorativeCardData);
  };

  return (
    <div className="w-full max-w-2xl mx-auto px-4 flex flex-col gap-4 relative">
      <div className="relative h-[250px] overflow-visible">
        {comingSoon && (
          <ComingSoonBadge className="absolute top-0 right-0 z-[60]" />
        )}
        <div className="relative h-full overflow-hidden">

        <motion.div
          className="absolute inset-0 flex items-center justify-center cursor-grab active:cursor-grabbing touch-pan-y"
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragEnd={onDragEnd}
          aria-label="Carrusel arrastrable; arrastra para cambiar de tarjeta"
        >
          {cards.map((card, index) => {
            const position = getCardPosition(index) as keyof typeof positions;
            return (
              <motion.div
                key={card.id}
                custom={position}
                variants={cardVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{
                  x: { type: 'spring', stiffness: 300, damping: 30 },
                  scale: { duration: 0.4 },
                  opacity: { duration: 0.3 },
                  filter: { duration: 0.3 },
                  rotateY: { duration: 0.4 },
                  zIndex: { duration: 0 },
                }}
                className="absolute origin-center will-change-transform"
                style={{
                  transformStyle: 'preserve-3d',
                  perspective: '1000px',
                  backfaceVisibility: 'hidden',
                }}
              >
                {renderCard(card, position)}
              </motion.div>
            );
          })}
        </motion.div>

        <button
          type="button"
          onClick={prevSlide}
          aria-label="Ir a la diapositiva anterior"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-black/50 p-2 rounded-full"
        >
          <ChevronLeft className="h-6 w-6 text-white" />
          <span className="sr-only">Anterior</span>
        </button>
        <button
          type="button"
          onClick={nextSlide}
          aria-label="Ir a la siguiente diapositiva"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-black/50 p-2 rounded-full"
        >
          <ChevronRight className="h-6 w-6 text-white" />
          <span className="sr-only">Siguiente</span>
        </button>
        </div>
      </div>
    </div>
  );
}
